import { useCallback, useEffect, useRef, useState } from 'react';
import { groupTripReviewApi } from '../../api/groupTripReviewApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import { ConfirmDialog } from '../../components/ui';
import {
  groupTripMemberFacingLabel,
  travelMemberProductDisplayName,
} from '../member-identity/memberIdentityPresentation';

/**
 * GroupTrip 評價區塊（TRAVEL-TRUST-01）。
 * 功能：
 *  - 顯示揪團 ACTIVE 評價列表（keyset 分頁）
 *  - 若已登入且有評價資格，顯示評價表單
 *  - 已提交評價者顯示「已評價」與刪除按鈕
 *
 * Props:
 *   groupTripId  {string|number} 揪團 ID
 *   hostMemberId {string|number} Host Member ID（用於顯示信譽聚合）
 *   tripPhase    {string}        行程狀態（COMPLETED 才顯示表單）
 *   visibility   {string}        揪團受眾（PUBLIC／FOLLOWERS／PRIVATE），決定送出前的可見範圍說明
 */
const AUDIENCE_DISCLOSURE_KEYS = {
  PUBLIC: 'travel.review.audiencePublic',
  FOLLOWERS: 'travel.review.audienceFollowers',
  PRIVATE: 'travel.review.audiencePrivate',
};

// 無權讀取與不存在皆不重試、不顯示任何評價資訊
const DENIED_HTTP_STATUSES = new Set([401, 403, 404]);

export default function GroupTripReviewSection({ groupTripId, hostMemberId, tripPhase, visibility }) {
  const { t } = useTravelTranslation();
  const { isAuthenticated } = useAuth();

  // ── 評價列表 ──
  const [reviews, setReviews] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [listPhase, setListPhase] = useState('loading'); // loading | ready | error | denied
  const [listError, setListError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);

  // ── 評價資格 ──
  const [eligibility, setEligibility] = useState(null); // null | { canReview, reasonKey, myReview }
  const [eligPhase, setEligPhase] = useState('idle'); // idle | loading | ready | error

  // ── 信譽聚合 ──
  const [reputation, setReputation] = useState(null);

  // ── 表單 ──
  const [rating, setRating] = useState(5);
  const [content, setContent] = useState('');
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const generation = useRef(0);

  // ─── 載入評價列表 ───────────────────────────────────────────
  const loadList = useCallback(async (cursor = null) => {
    const token = ++generation.current;
    if (!cursor) setListPhase('loading');
    else setLoadingMore(true);
    setListError('');
    try {
      const data = await groupTripReviewApi.list(groupTripId, { cursor, limit: 20 });
      if (token !== generation.current) return;
      setReviews((prev) => cursor ? [...prev, ...(data.items || [])] : (data.items || []));
      setNextCursor(data.nextCursor || null);
      setListPhase('ready');
    } catch (error) {
      if (token !== generation.current) return;
      if (DENIED_HTTP_STATUSES.has(error?.httpStatus)) {
        setReviews([]);
        setNextCursor(null);
        setListPhase('denied');
        return;
      }
      setListError(t('travel.review.loadFailed'));
      setListPhase('error');
    } finally {
      if (cursor) setLoadingMore(false);
    }
  }, [groupTripId, t]);

  useEffect(() => {
    loadList();
    return () => { generation.current += 1; };
  }, [loadList]);

  // ─── 載入信譽聚合 ────────────────────────────────────────────
  useEffect(() => {
    if (!hostMemberId) return;
    groupTripReviewApi.hostReputation(hostMemberId)
      .then((data) => setReputation(data))
      .catch(() => {});
  }, [hostMemberId]);

  // ─── 載入評價資格（已登入 + 行程已完成） ─────────────────────
  useEffect(() => {
    if (!isAuthenticated || tripPhase !== 'COMPLETED') {
      setEligPhase('idle');
      return;
    }
    setEligPhase('loading');
    groupTripReviewApi.eligibility(groupTripId)
      .then((data) => { setEligibility(data); setEligPhase('ready'); })
      .catch(() => setEligPhase('error'));
  }, [isAuthenticated, groupTripId, tripPhase]);

  // ─── 提交評價 ────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (submitBusy) return;
    setSubmitBusy(true);
    setSubmitError('');
    try {
      const review = await groupTripReviewApi.create(groupTripId, {
        rating,
        content: content.trim() || null,
      });
      setEligibility((prev) => ({ ...prev, canReview: false, myReview: review }));
      setReviews((prev) => [review, ...prev]);
      setContent('');
    } catch (err) {
      const key = err?.raw?.key;
      setSubmitError(resolveReviewError(key, t) || t('travel.review.submitFailed'));
    } finally {
      setSubmitBusy(false);
    }
  }

  const [pendingDeleteReviewId, setPendingDeleteReviewId] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // ─── 刪除評價（ConfirmDialog；mutation 語意不變）────────────────
  function requestDelete(reviewId) {
    if (deleteBusy) return;
    setPendingDeleteReviewId(reviewId);
  }

  async function confirmDelete() {
    const reviewId = pendingDeleteReviewId;
    if (!reviewId || deleteBusy) return;
    setDeleteBusy(true);
    try {
      await groupTripReviewApi.remove(groupTripId, reviewId);
      setEligibility((prev) => ({ ...prev, canReview: false, myReview: null }));
      setReviews((prev) => prev.filter((r) => r.reviewId !== reviewId));
      setPendingDeleteReviewId(null);
    } catch (err) {
      const key = err?.raw?.key;
      alert(resolveReviewError(key, t) || t('travel.review.deleteFailed'));
    } finally {
      setDeleteBusy(false);
    }
  }

  const isCompleted = tripPhase === 'COMPLETED';
  const myReview = eligibility?.myReview;
  const canReview = eligibility?.canReview === true;

  return (
    <section className="group-trip-review-section">
      {/* ── 信譽聚合 ── */}
      {reputation && (
        <div className="review-reputation">
          <span className="review-reputation__stars">
            {reputation.averageRating
              ? renderStars(parseFloat(reputation.averageRating))
              : t('travel.review.noRatingYet')}
          </span>
          {reputation.reviewCount > 0 && (
            <span className="review-reputation__count">
              {t('travel.review.reviewCount').replace('{count}', reputation.reviewCount)}
            </span>
          )}
        </div>
      )}

      <h3 className="review-section__title">{t('travel.review.sectionTitle')}</h3>

      {/* ── 評價資格 / 已提交評價 / 表單 ── */}
      {isAuthenticated && isCompleted && (
        <div className="review-form-area">
          {eligPhase === 'loading' && <p>{t('travel.common.loading')}</p>}
          {eligPhase === 'error' && <p className="error-text">{t('travel.review.eligibilityLoadFailed')}</p>}
          {eligPhase === 'ready' && (
            <>
              {myReview && (
                <div className="my-review-card">
                  <p>{t('travel.review.alreadyReviewed')}</p>
                  <ReviewCard review={myReview} t={t} />
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => requestDelete(myReview.reviewId)}
                  >
                    {t('travel.review.deleteReview')}
                  </button>
                </div>
              )}
              {canReview && !myReview && (
                <form onSubmit={handleSubmit} className="review-form">
                  <p className="review-form__hint">{t('travel.review.eligibleHint')}</p>
                  <p className="review-form__audience" data-testid="review-audience-disclosure">
                    {t(AUDIENCE_DISCLOSURE_KEYS[visibility] || AUDIENCE_DISCLOSURE_KEYS.PRIVATE)}
                  </p>
                  <div className="review-form__stars">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        aria-label={`${n} star`}
                        className={n <= rating ? 'star star--filled' : 'star'}
                        onClick={() => setRating(n)}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                  <textarea
                    className="review-form__content"
                    placeholder={t('travel.review.contentPlaceholder')}
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    maxLength={4000}
                    rows={3}
                  />
                  {submitError && <p className="error-text">{submitError}</p>}
                  <button type="submit" className="btn btn--primary" disabled={submitBusy}>
                    {submitBusy ? t('travel.review.submitting') : t('travel.review.submitReview')}
                  </button>
                </form>
              )}
              {!canReview && !myReview && eligibility && (
                <p className="review-not-eligible">{t('travel.review.notEligible')}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* ── 評價列表 ── */}
      {listPhase === 'loading' && <p>{t('travel.common.loading')}</p>}
      {listPhase === 'denied' && (
        <p className="review-unavailable" data-testid="review-list-unavailable">
          {t('travel.review.listUnavailable')}
        </p>
      )}
      {listPhase === 'error' && (
        <p className="error-text">
          {listError}&nbsp;
          <button type="button" onClick={() => loadList()}>{t('travel.common.retry')}</button>
        </p>
      )}
      {listPhase === 'ready' && (
        <>
          {reviews.length === 0 && <p className="review-empty">{t('travel.review.noReviews')}</p>}
          <ul className="review-list">
            {reviews.map((r) => (
              <li key={r.reviewId} className="review-list__item">
                <ReviewCard review={r} t={t} />
              </li>
            ))}
          </ul>
          {nextCursor && (
            <button
              type="button"
              className="btn btn--ghost"
              disabled={loadingMore}
              onClick={() => loadList(nextCursor)}
            >
              {loadingMore ? t('travel.common.loading') : t('travel.review.loadMore')}
            </button>
          )}
        </>
      )}

      <ConfirmDialog
        open={pendingDeleteReviewId != null}
        title={t('travel.review.deleteReview')}
        description={t('travel.review.confirmDelete')}
        confirmLabel={t('travel.review.deleteReview')}
        cancelLabel={t('travel.safety.cancel')}
        danger
        loading={deleteBusy}
        onCancel={() => {
          if (!deleteBusy) setPendingDeleteReviewId(null);
        }}
        onConfirm={confirmDelete}
      />
    </section>
  );
}

function ReviewCard({ review, t }) {
  return (
    <div className="review-card">
      <div className="review-card__header">
        {review.reviewerAvatarUrl && (
          <img
            src={review.reviewerAvatarUrl}
            alt={review.reviewerDisplayName || String(review.reviewerMemberId)}
            className="review-card__avatar"
          />
        )}
        <span className="review-card__name">
          {review.reviewerDisplayName
            ? travelMemberProductDisplayName({ displayName: review.reviewerDisplayName })
            : groupTripMemberFacingLabel({ role: 'companion', memberId: review.reviewerMemberId })}
        </span>
        <span className="review-card__stars" aria-label={`${review.rating} stars`}>
          {renderStars(review.rating)}
        </span>
        <time className="review-card__date">
          {review.createdAt ? review.createdAt.slice(0, 10) : ''}
        </time>
      </div>
      {review.content && <p className="review-card__content">{review.content}</p>}
    </div>
  );
}

function renderStars(rating) {
  const full = Math.round(rating);
  return '★'.repeat(Math.max(0, Math.min(5, full))) + '☆'.repeat(Math.max(0, 5 - full));
}

function resolveReviewError(key, t) {
  if (!key) return null;
  // 後端錯誤 key 格式：travel.review.xxx 或 travel.review.xxx.yyy
  // 直接使用後端回傳的 key（travel.review.* 路徑）呼叫 translate
  const translated = t(key);
  return translated !== key ? translated : null;
}
